import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import type { Language } from '../contexts/LanguageContext';
import {
  GENERATION_VENUE,
  QF_SCHEMATIC,
  applyKnockoutAdvancement,
  computeKnockoutBracket,
  formatPairLabel,
  formatSchematicPair,
  resolveSchematicTeam,
  getGenerationFormat,
  getGenerationSchedule,
  physicalPitch,
  pitchesLabelForGeneration,
  playoffSchematic,
  slotEndLabel,
  slotKickoffLabel,
  medalCeremonyStartClock,
  medalCeremonyApproxEndClock2014,
  type GenerationYear,
  type GroupMatchDef,
  type KoMatchDef,
  type KoLabelKey,
  type MatchDayKey,
  type ScorePair,
  type SchematicResolveContext,
  type TeamNamesMap,
} from '../data/cupScoresData';
import {
  type CupScoresStrings,
  getCupScoresLocale,
  getCupScoresRoleLabels,
} from '../data/cupScoresLocale';

const PDF_FONT = 'NotoSans';
const FONT_REGULAR = 'NotoSans-Regular.ttf';
const FONT_BOLD = 'NotoSans-Bold.ttf';

const BRAND_ORANGE: [number, number, number] = [242, 101, 20];
const TEXT_DARK: [number, number, number] = [40, 38, 45];
const TEXT_MUTED: [number, number, number] = [100, 98, 94];
const ROW_ALT: [number, number, number] = [248, 246, 242];
const GRID_LINE: [number, number, number] = [220, 218, 212];

const DAY_HEADING: Record<'en' | 'sr', Record<MatchDayKey, string>> = {
  en: {
    may23: 'Saturday, 23 May 2026',
    may24: 'Sunday, 24 May 2026',
  },
  sr: {
    may23: 'Subota, 23. maj 2026.',
    may24: 'Nedelja, 24. maj 2026.',
  },
};

const PO_INDEX: Record<'po1' | 'po2', number> = { po1: 0, po2: 1 };
const QF_INDEX: Record<'qf1' | 'qf2' | 'qf3' | 'qf4', number> = {
  qf1: 0,
  qf2: 1,
  qf3: 2,
  qf4: 3,
};
const KO_SLOT_LETTERS = ['E', 'F', 'G', 'H'] as const;

export type CupSchedulePdfOptions = {
  lang: Language;
  /** Samo izabrano godište na ekranu. */
  generation: GenerationYear;
  teamNames?: TeamNamesMap;
  results?: Record<string, ScorePair | undefined>;
};

type SchedulePdfRow3 = [string, string, string];
type SchedulePdfRow4 = [string, string, string, string];

function dayHeading(lang: Language, day: MatchDayKey): string {
  const key = lang === 'sr' ? 'sr' : 'en';
  return DAY_HEADING[key][day];
}

function arrayBufferToBase64(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer);
  const chunk = 0x8000;
  let binary = '';
  for (let i = 0; i < bytes.length; i += chunk) {
    const slice = bytes.subarray(i, i + chunk);
    for (let j = 0; j < slice.length; j += 1) {
      binary += String.fromCharCode(slice[j]);
    }
  }
  return btoa(binary);
}

async function registerUnicodeFonts(doc: jsPDF): Promise<void> {
  const base = (process.env.PUBLIC_URL ?? '').replace(/\/$/, '');
  const [regBuf, boldBuf] = await Promise.all([
    fetch(`${base}/fonts/${FONT_REGULAR}`).then((r) => {
      if (!r.ok) throw new Error(`Font load failed: ${FONT_REGULAR}`);
      return r.arrayBuffer();
    }),
    fetch(`${base}/fonts/${FONT_BOLD}`).then((r) => {
      if (!r.ok) throw new Error(`Font load failed: ${FONT_BOLD}`);
      return r.arrayBuffer();
    }),
  ]);

  doc.addFileToVFS(FONT_REGULAR, arrayBufferToBase64(regBuf));
  doc.addFont(FONT_REGULAR, PDF_FONT, 'normal');
  doc.addFileToVFS(FONT_BOLD, arrayBufferToBase64(boldBuf));
  doc.addFont(FONT_BOLD, PDF_FONT, 'bold');
}

function setPdfFont(doc: jsPDF, style: 'normal' | 'bold' = 'normal') {
  doc.setFont(PDF_FONT, style);
}

function koPdfLabels(
  labelKey: KoLabelKey,
  gen: GenerationYear,
  ctx: SchematicResolveContext,
  L: CupScoresStrings,
  teamNames: TeamNamesMap,
  results: Record<string, ScorePair | undefined>
): { phase: string; match: string } {
  const po = playoffSchematic(gen);
  const schematic = (h: string, a: string) => formatSchematicPair(h, a, ctx, ' v ');
  const fmt = getGenerationFormat(gen);
  const bracket = applyKnockoutAdvancement(computeKnockoutBracket(results, gen), results, gen);
  const pairLabel = (p: { home: string; away: string }) =>
    p.home && p.away ? formatPairLabel(p.home, p.away, teamNames, gen, ' v ') : '—';

  if (labelKey === 'po1' || labelKey === 'po2') {
    const letter = KO_SLOT_LETTERS[PO_INDEX[labelKey]];
    const [h, a] = po[labelKey];
    return { phase: L.koLabels[labelKey], match: `${letter} · ${schematic(h, a)}` };
  }
  if (labelKey === 'qf1' || labelKey === 'qf2' || labelKey === 'qf3' || labelKey === 'qf4') {
    const letter = KO_SLOT_LETTERS[QF_INDEX[labelKey]];
    if (bracket?.format === 'quarter_finals') {
      const p = bracket.qf[QF_INDEX[labelKey]];
      return {
        phase: L.koLabels[labelKey],
        match: `${letter} · ${formatPairLabel(p.home, p.away, teamNames, gen, ' v ')}`,
      };
    }
    const [h, a] = QF_SCHEMATIC[labelKey];
    return { phase: L.koLabels[labelKey], match: `${letter} · ${schematic(h, a)}` };
  }
  if (labelKey === 'sf1' || labelKey === 'sf2') {
    const ix = labelKey === 'sf1' ? 0 : 1;
    if (bracket?.format === 'quarter_finals' || bracket?.format === 'semi_direct') {
      const p = bracket.sf[ix]!;
      if (p.home && p.away) {
        return { phase: L.koLabels[labelKey], match: pairLabel(p) };
      }
    }
    if (fmt.knockout === 'semi_direct') {
      const [h, a] = labelKey === 'sf1' ? po.po1 : po.po2;
      const fixed = labelKey === 'sf1' ? resolveSchematicTeam('B1', ctx) : resolveSchematicTeam('A1', ctx);
      const poS = formatSchematicPair(h, a, ctx, '–');
      const letter = labelKey === 'sf1' ? 'E' : 'F';
      return {
        phase: L.koLabels[labelKey],
        match: `${fixed} v ${ctx.labels.rolePlayoffWinner(letter)} (${poS})`,
      };
    }
    return {
      phase: L.koLabels[labelKey],
      match: labelKey === 'sf1' ? L.bracketSemi1Pair : L.bracketSemi2Pair,
    };
  }
  if (labelKey === 'final' || labelKey === 'third') {
    if (bracket?.format === 'league_playoffs') {
      const pair = labelKey === 'final' ? bracket.final : bracket.thirdPlaceMatch;
      return { phase: L.koLabels[labelKey], match: pairLabel(pair) };
    }
    if (bracket?.format === 'quarter_finals' || bracket?.format === 'semi_direct') {
      const pair = labelKey === 'final' ? bracket.final : bracket.thirdPlaceMatch;
      if (pair.home && pair.away) {
        return { phase: L.koLabels[labelKey], match: pairLabel(pair) };
      }
    }
  }
  return { phase: L.koLabels[labelKey], match: '—' };
}

function groupPhaseLabel(gen: GenerationYear, group: string, L: CupScoresStrings): string {
  const k = getGenerationFormat(gen).knockout;
  if (k === 'league_table' || k === 'league_playoffs') {
    return L.pdfPhaseMiniLeague;
  }
  return `${L.groupPrefix} ${group}`;
}

function buildGenerationRows(
  gen: GenerationYear,
  teamNames: TeamNamesMap,
  results: Record<string, ScorePair | undefined>,
  L: CupScoresStrings,
  ctx: SchematicResolveContext
): { withPhase: boolean; rows: SchedulePdfRow3[] | SchedulePdfRow4[] } {
  const schedule = getGenerationSchedule(gen);
  const withPhase = getGenerationFormat(gen).knockout !== 'league_table';
  const rows3: SchedulePdfRow3[] = [];
  const rows4: SchedulePdfRow4[] = [];

  for (let slot = 0; slot <= schedule.koLastSlot; slot += 1) {
    const time = `${slotKickoffLabel(gen, slot)}–${slotEndLabel(gen, slot)}`;
    type SlotItem =
      | { kind: 'group'; m: GroupMatchDef }
      | { kind: 'ko'; k: KoMatchDef };
    const items: SlotItem[] = [
      ...schedule.groupMatches.filter((m) => m.startSlot === slot).map((m) => ({ kind: 'group' as const, m })),
      ...schedule.koMatches.filter((k) => k.startSlot === slot).map((k) => ({ kind: 'ko' as const, k })),
    ];
    items.sort((a, b) => {
      const laneA = a.kind === 'group' ? a.m.lane : a.k.lane;
      const laneB = b.kind === 'group' ? b.m.lane : b.k.lane;
      const pitchA = physicalPitch(gen, laneA, slot);
      const pitchB = physicalPitch(gen, laneB, slot);
      return pitchA - pitchB || laneA - laneB;
    });

    for (const item of items) {
      const pitch = String(physicalPitch(gen, item.kind === 'group' ? item.m.lane : item.k.lane, slot));
      if (item.kind === 'group') {
        const { m } = item;
        const match = formatPairLabel(m.home, m.away, teamNames, gen, ' v ');
        if (withPhase) {
          rows4.push([time, pitch, match, groupPhaseLabel(gen, m.group, L)]);
        } else {
          rows3.push([time, pitch, match]);
        }
      } else {
        const { k } = item;
        const { phase, match } = koPdfLabels(k.labelKey, gen, ctx, L, teamNames, results);
        if (withPhase) {
          rows4.push([time, pitch, match, phase]);
        } else {
          rows3.push([time, pitch, match]);
        }
      }
    }
  }

  const medalStart = medalCeremonyStartClock(gen);
  const medalTime =
    gen === 2014 ? `${medalStart}–${medalCeremonyApproxEndClock2014()}` : medalStart;
  if (withPhase) {
    rows4.push([medalTime, '—', L.medalCeremonyTitle, L.medalCeremonyLead]);
  } else {
    rows3.push([medalTime, '—', L.medalCeremonyTitle]);
  }

  return { withPhase, rows: withPhase ? rows4 : rows3 };
}

function pdfFilename(lang: Language, gen: GenerationYear): string {
  return lang === 'sr'
    ? `Junior-Cup-${gen}-raspored.pdf`
    : `Junior-Cup-${gen}-schedule.pdf`;
}

function drawPageFooter(
  doc: jsPDF,
  L: CupScoresStrings,
  gen: GenerationYear,
  lang: Language,
  pageNumber: number,
  totalPages: number
) {
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();
  const day = GENERATION_VENUE[gen].matchDay;
  setPdfFont(doc, 'normal');
  doc.setFontSize(7.5);
  doc.setTextColor(...TEXT_MUTED);
  doc.text(
    `${L.pdfFooter} · ${L.pdfGenLabel} ${gen} · ${dayHeading(lang, day)} · ${pageNumber}/${totalPages}`,
    pageW / 2,
    pageH - 7,
    { align: 'center' }
  );
}

/** PDF raspored — samo izabrano godište, jedna tabela bez naslovne strane. */
export async function exportCupSchedulePdf(options: CupSchedulePdfOptions): Promise<void> {
  const { lang, generation: gen, teamNames = {}, results = {} } = options;
  const L = getCupScoresLocale(lang);
  const roleLabels = getCupScoresRoleLabels(lang);

  const ctx: SchematicResolveContext = {
    results,
    names: teamNames,
    generation: gen,
    labels: roleLabels,
  };
  const { withPhase, rows } = buildGenerationRows(gen, teamNames, results, L, ctx);

  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
  await registerUnicodeFonts(doc);

  const pageW = doc.internal.pageSize.getWidth();
  const margin = { left: 14, right: 14, top: 14, bottom: 16 };
  const day = GENERATION_VENUE[gen].matchDay;
  const pitches = pitchesLabelForGeneration(gen);

  let y = margin.top;

  setPdfFont(doc, 'bold');
  doc.setFontSize(14);
  doc.setTextColor(...TEXT_DARK);
  doc.text(`${L.pdfGenLabel} ${gen}`, margin.left, y);

  setPdfFont(doc, 'normal');
  doc.setFontSize(9.5);
  doc.setTextColor(...TEXT_MUTED);
  const meta = `${dayHeading(lang, day)}  ·  ${L.pdfPitchesLabel}: ${pitches}`;
  doc.text(meta, margin.left, y + 6);

  y += 14;

  const head = withPhase
    ? [[L.pdfColTime, L.pdfColPitch, L.pdfColMatch, L.pdfColPhase]]
    : [[L.pdfColTime, L.pdfColPitch, L.pdfColMatch]];

  autoTable(doc, {
    startY: y,
    head,
    body: rows as string[][],
    margin: { left: margin.left, right: margin.right, bottom: margin.bottom },
    theme: 'plain',
    styles: {
      font: PDF_FONT,
      fontSize: 9,
      cellPadding: { top: 2.8, bottom: 2.8, left: 3, right: 3 },
      lineColor: GRID_LINE,
      lineWidth: 0.12,
      textColor: TEXT_DARK,
      overflow: 'linebreak',
      valign: 'middle',
    },
    headStyles: {
      font: PDF_FONT,
      fontStyle: 'bold',
      fillColor: BRAND_ORANGE,
      textColor: [255, 255, 255],
      fontSize: 8.5,
      cellPadding: { top: 3.2, bottom: 3.2, left: 3, right: 3 },
    },
    columnStyles: withPhase
      ? {
          0: { cellWidth: 28 },
          1: { cellWidth: 18, halign: 'center' },
          2: { cellWidth: 'auto' },
          3: { cellWidth: 36 },
        }
      : {
          0: { cellWidth: 30 },
          1: { cellWidth: 20, halign: 'center' },
          2: { cellWidth: 'auto' },
        },
    alternateRowStyles: { fillColor: ROW_ALT },
    didDrawPage: (data) => {
      const total = doc.getNumberOfPages();
      drawPageFooter(doc, L, gen, lang, data.pageNumber, total);
    },
  });

  doc.save(pdfFilename(lang, gen));
}
